/**
 * Shared click-handling for elements that represent navigation (issue #339).
 *
 * The correct way to support middle-click / ctrl-click / cmd-click / right-click
 * "open in new tab" on a nav item is to render a real `<a href="...">` (MUI
 * components take `component="a"` + `href`), then intercept ONLY the plain
 * left click to keep the existing in-app SPA navigation (preventDefault + call
 * the app's own navigate/select function) — every other kind of click is left
 * alone so the browser's native new-tab/new-window/context-menu handling does
 * its job, exactly as it would for any other link on the web:
 *
 *  - A real middle-click fires `auxclick`, not `click`, in every evergreen
 *    browser, so it never even reaches an onClick handler bound to `click` —
 *    it "just works" as long as the element has a real `href`.
 *  - ctrl-click / cmd-click / shift-click fire `click` with the modifier flag
 *    set; the handler below must NOT call `preventDefault()` in that case, or
 *    the browser's native "open in a new tab/window" is cancelled.
 *  - A non-primary-button click (`event.button !== 0`, e.g. a real middle
 *    click on the rare browser that does dispatch `click` for it) is treated
 *    the same way.
 */

/** True only for an unmodified, primary-button left click. */
export function isPlainLeftClick(event) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * Build an onClick handler for a real `<a href>` (or an MUI component rendered
 * `component="a"`): on a plain left click it calls `preventDefault()` then
 * `onNavigate(event)` (the existing SPA navigation side effect); any modified
 * click, non-primary button, or right-click is left untouched so the browser's
 * native behaviour (new tab / new window / context menu) applies.
 *
 * @param {(event: React.SyntheticEvent) => void} onNavigate
 * @returns {(event: React.SyntheticEvent) => void}
 */
export function navClickHandler(onNavigate) {
  return (event) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onNavigate(event);
  };
}
